import React, { useEffect, useState } from "react";
import { Users, Plus, Trash2, Bell, Check, ArrowRight, MessageCircle, Clock } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAgencyNavigate, useAgencyPath, useAgencySlug } from "../lib/agency";
import { useSettings } from "../lib/settings";
import {
  getNotificationPermission,
  requestNotificationPermission,
  showLocalNotification,
  getPushToken,
  getPlatform,
} from "../lib/native";

const STORAGE_GROUP = "ts_my_group";
const PHONE_RE = /^\+?[0-9\s\-()]{7,20}$/;

function phoneError(phone) {
  const v = (phone || "").trim();
  if (!PHONE_RE.test(v)) {
    return "Teléfono inválido. Usa solo números, espacios, guiones y '+' al inicio (ej: +52 555 123 4567).";
  }
  if (v.replace(/\D/g, "").length < 7) {
    return "El teléfono debe tener al menos 7 dígitos.";
  }
  return null;
}

function extractApiError(e, fallback) {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length) {
    // Errores de validación de FastAPI/Pydantic (422)
    return detail.map((d) => d.msg).join(" · ");
  }
  return fallback;
}

export default function Groups() {
  const navigate = useAgencyNavigate();
  const toAgencyPath = useAgencyPath();
  const agencySlug = useAgencySlug();
  const { settings } = useSettings();
  const saved = JSON.parse(localStorage.getItem(STORAGE_GROUP) || "null");
  const [step, setStep] = useState(saved ? "ready" : "create");
  const [name, setName] = useState("");
  const [creatorName, setCreatorName] = useState("");
  const [creatorPhone, setCreatorPhone] = useState("");
  const [creatorEmail, setCreatorEmail] = useState("");
  const [members, setMembers] = useState([{ name: "", phone: "" }, { name: "", phone: "" }, { name: "", phone: "" }]);
  const [group, setGroup] = useState(saved?.group || null);
  const [err, setErr] = useState("");
  const [pushOk, setPushOk] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    getNotificationPermission().then((p) => setPushOk(p === "granted"));
  }, []);

  useEffect(() => {
    if (step !== "ready" || !group) return;
    const loadStatus = () => {
      api.get(`/groups/${group.id}/status`).then(({ data }) => setStatus(data)).catch(() => {});
    };
    loadStatus();
    const i = setInterval(loadStatus, 8000);
    return () => clearInterval(i);
  }, [step, group?.id]);

  const addMember = () => setMembers([...members, { name: "", phone: "" }]);
  const removeMember = (i) => setMembers(members.filter((_, idx) => idx !== i));
  const updateMember = (i, key, val) => {
    const copy = [...members];
    copy[i][key] = val;
    setMembers(copy);
  };

  const create = async () => {
    setErr("");
    const filled = members.filter((m) => m.name && m.phone);
    if (!creatorName || !creatorPhone || !name) {
      setErr("Completa el nombre del grupo y tus datos.");
      return;
    }
    const creatorPhoneErr = phoneError(creatorPhone);
    if (creatorPhoneErr) {
      setErr(`Tu teléfono: ${creatorPhoneErr}`);
      return;
    }
    if (filled.length < 3) {
      setErr("Necesitas 3 miembros adicionales (4 personas en total).");
      return;
    }
    for (const m of filled) {
      const mErr = phoneError(m.phone);
      if (mErr) {
        setErr(`Teléfono de ${m.name || "un miembro"}: ${mErr}`);
        return;
      }
    }
    try {
      const { data } = await api.post("/groups", {
        name, members: filled, creator_name: creatorName, creator_phone: creatorPhone,
        creator_email: creatorEmail, agency_slug: agencySlug,
      });
      setGroup(data);
      // myName: quién es ESTE dispositivo dentro del grupo (el creador). Sin
      // esto, SOSPage no podía distinguir "yo lo mandé" de "alguien más del
      // grupo lo mandó".
      localStorage.setItem(STORAGE_GROUP, JSON.stringify({ group: data, myName: creatorName }));
      setStep("push");
    } catch (e) {
      setErr(extractApiError(e, "Error al crear"));
    }
  };

  const enablePush = async () => {
    setErr("");
    const perm = await requestNotificationPermission();
    if (perm !== "granted") {
      setErr("Debes permitir notificaciones para activar el SOS.");
      return;
    }
    const endpointId = localStorage.getItem("ts_endpoint_id") ||
      `web-${Math.random().toString(36).slice(2)}-${Date.now()}`;
    localStorage.setItem("ts_endpoint_id", endpointId);

    await api.post(`/groups/${group.id}/unlock-sos`, {
      endpoint_id: endpointId,
      user_agent: navigator.userAgent,
      group_id: group.id,
    });

    // Registro real de FCM: así este dispositivo recibe push cuando OTRO
    // miembro del grupo active el SOS (aunque esta app esté cerrada).
    try {
      const fcmToken = await getPushToken();
      if (fcmToken) {
        await api.post(`/groups/${group.id}/register-device`, {
          fcm_token: fcmToken,
          member_name: creatorName || "Miembro",
          platform: getPlatform(),
        });
      }
    } catch (e) {
      console.error("No se pudo registrar el dispositivo para push:", e);
      // No bloqueamos el flujo: el SOS local (para quien lo activa) sigue funcionando
      // aunque el push a los demás miembros no haya podido registrarse.
    }

    setPushOk(true);
    // Show persistent welcome notification
    showLocalNotification(
      "SOS Activado",
      `Tu grupo de ${settings.agency_name} está protegido. Mantén la app accesible.`,
      { tag: "cherry-welcome" }
    );
    localStorage.setItem(STORAGE_GROUP, JSON.stringify({ group: { ...group, sos_unlocked: true } }));
    setStep("ready");
  };

  return (
    <Layout>
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-12">
        <div className="mb-10 fade-up">
          <p className="text-xs uppercase tracking-[0.4em] text-[var(--brand-cyan,#FF85AC)] mb-3">Grupos de Emergencia</p>
          <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight">
            Crea tu <span className="text-[var(--brand-cyan,#FF85AC)]">grupo SOS</span>
          </h1>
          <p className="text-neutral-400 mt-3">
            Necesitas mínimo 4 personas. Tras crearlo, activa las notificaciones push para desbloquear el SOS gratis.
          </p>
        </div>

        {step === "create" && (
          <div className="glass rounded-3xl p-8 border border-white/10 space-y-6 fade-up">
            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-2">Nombre del grupo</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Familia García en Cancún"
                data-testid="group-name-input"
                className="w-full bg-transparent border-b border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none py-2 text-white"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-2">Tu nombre</label>
                <input
                  value={creatorName}
                  onChange={(e) => setCreatorName(e.target.value)}
                  data-testid="creator-name-input"
                  className="w-full bg-transparent border-b border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none py-2 text-white"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-2">Tu teléfono</label>
                <input
                  value={creatorPhone}
                  onChange={(e) => setCreatorPhone(e.target.value)}
                  placeholder="+52 555 1234567"
                  data-testid="creator-phone-input"
                  className="w-full bg-transparent border-b border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none py-2 text-white"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-2">Tu correo (opcional)</label>
                <input
                  type="email"
                  value={creatorEmail}
                  onChange={(e) => setCreatorEmail(e.target.value)}
                  placeholder="tu@correo.com"
                  data-testid="creator-email-input"
                  className="w-full bg-transparent border-b border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none py-2 text-white"
                />
                <p className="text-[10px] text-neutral-600 mt-1.5">
                  Te mandamos ahí el aviso legal del SOS y grupos de emergencia.
                </p>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Miembros adicionales (mín. 3)</p>
                <button
                  type="button"
                  onClick={addMember}
                  data-testid="add-member-btn"
                  className="text-[10px] uppercase tracking-[0.25em] text-[var(--brand-cyan,#FF85AC)] hover:underline inline-flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Añadir
                </button>
              </div>
              <div className="space-y-3">
                {members.map((m, i) => (
                  <div key={i} className="flex gap-2" data-testid={`member-row-${i}`}>
                    <input
                      value={m.name}
                      onChange={(e) => updateMember(i, "name", e.target.value)}
                      placeholder={`Miembro ${i + 1}`}
                      className="flex-1 bg-black/40 rounded-xl px-3 py-2 text-sm border border-white/10 focus:border-[var(--brand-cyan,#FF85AC)] outline-none"
                    />
                    <input
                      value={m.phone}
                      onChange={(e) => updateMember(i, "phone", e.target.value)}
                      placeholder="Teléfono"
                      className="flex-1 bg-black/40 rounded-xl px-3 py-2 text-sm border border-white/10 focus:border-[var(--brand-cyan,#FF85AC)] outline-none"
                    />
                    {members.length > 3 && (
                      <button onClick={() => removeMember(i)} className="p-2 text-neutral-500 hover:text-[#FF3B30]">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {err && <p className="text-sm text-[#FF3B30]" data-testid="group-error">{err}</p>}

            <button
              onClick={create}
              data-testid="create-group-btn"
              className="w-full bg-[var(--brand-cyan,#FF85AC)] text-black py-3.5 rounded-full font-semibold hover:bg-[#FF9DC0] cyan-glow"
            >
              Crear Grupo
            </button>
          </div>
        )}

        {step === "push" && group && (
          <div className="glass rounded-3xl p-10 border border-white/10 text-center fade-up">
            <div className="inline-flex w-16 h-16 rounded-full bg-[var(--brand-cyan,#FF85AC)]/10 border border-[var(--brand-cyan,#FF85AC)]/40 items-center justify-center mb-6">
              <Bell className="w-6 h-6 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.4} />
            </div>
            <h2 className="font-display text-3xl font-light mb-3">Casi listo</h2>
            <p className="text-neutral-400 mb-2 max-w-md mx-auto">
              Para activar el <span className="text-[var(--brand-cyan,#FF85AC)]">SOS gratis</span> debes permitir las
              notificaciones push. Solo se usan para avisarte si alguien de tu grupo activa el SOS —
              nunca para promociones.
            </p>
            {err && <p className="text-sm text-[#FF3B30] my-3" data-testid="push-error">{err}</p>}
            <button
              onClick={enablePush}
              data-testid="enable-push-btn"
              className="mt-4 inline-flex items-center gap-2 bg-[var(--brand-cyan,#FF85AC)] text-black px-7 py-3.5 rounded-full font-semibold hover:bg-[#FF9DC0] cyan-glow"
            >
              Activar Notificaciones <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {step === "ready" && group && (
          <div className="space-y-6 fade-up">
            {/* Invitación — primero y bien visible: es el paso que falta para que el grupo sirva de algo */}
            <div className="rounded-3xl border-2 border-[var(--brand-cyan,#FF85AC)] bg-[var(--brand-cyan,#FF85AC)]/5 p-6" data-testid="invite-card">
              <div className="flex items-center gap-2 mb-2">
                <Bell className="w-4 h-4 text-[var(--brand-cyan,#FF85AC)]" />
                <p className="text-xs uppercase tracking-[0.3em] text-[var(--brand-cyan,#FF85AC)] font-bold">
                  Falta invitar a tu grupo
                </p>
              </div>
              <p className="text-sm text-neutral-300 mb-4">
                Escribir su nombre y teléfono <b className="text-white">no los agrega automáticamente</b> —
                cada persona debe abrir su propio link, elegir su nombre y aceptar notificaciones desde
                <b className="text-white"> su propio celular</b>. Mándaselo directo por WhatsApp:
              </p>

              <div className="space-y-2 mb-4">
                {(status?.members || []).map((m, i) => {
                  const url = `${window.location.origin}${toAgencyPath(`/groups/join/${group.id}`)}`;
                  const text = `Hola ${m.name.split(" ")[0]}, únete a nuestro grupo de emergencia "${group.name}" en Travel Shield para recibir alertas SOS: ${url}`;
                  const waLink = `https://wa.me/${m.phone.replace(/\D/g, "")}?text=${encodeURIComponent(text)}`;
                  return (
                    <div key={i} className="flex items-center justify-between gap-3 bg-black/30 rounded-xl px-4 py-2.5" data-testid={`member-status-${i}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        {m.joined ? (
                          <Check className="w-4 h-4 text-[var(--brand-cyan,#FF85AC)] shrink-0" />
                        ) : (
                          <Clock className="w-4 h-4 text-neutral-600 shrink-0" />
                        )}
                        <span className="text-sm text-white truncate">{m.name}</span>
                        {m.name.trim().toLowerCase() === group.creator_name.trim().toLowerCase() && (
                          <span className="text-[10px] text-neutral-500 shrink-0">(Tú)</span>
                        )}
                      </div>
                      {m.joined ? (
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--brand-cyan,#FF85AC)] shrink-0">Conectado</span>
                      ) : (
                        <a
                          href={waLink}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`whatsapp-invite-${i}`}
                          className="shrink-0 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] bg-[#25D366] text-black px-3 py-1.5 rounded-full font-semibold hover:brightness-95"
                        >
                          <MessageCircle className="w-3 h-3" /> Invitar
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>

              {status && (
                <p className="text-xs text-neutral-500 mb-4">
                  {status.joined_count} de {status.total} conectados para recibir alertas.
                </p>
              )}

              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={`${window.location.origin}${toAgencyPath(`/groups/join/${group.id}`)}`}
                  data-testid="join-link-input"
                  onFocus={(e) => e.target.select()}
                  className="flex-1 bg-black/40 border border-white/15 rounded-full px-4 py-2 text-xs text-neutral-300 truncate"
                />
                <button
                  onClick={async () => {
                    const url = `${window.location.origin}${toAgencyPath(`/groups/join/${group.id}`)}`;
                    if (navigator.share) {
                      try { await navigator.share({ title: group.name, url }); return; } catch {}
                    }
                    navigator.clipboard?.writeText(url);
                    setLinkCopied(true);
                    setTimeout(() => setLinkCopied(false), 2000);
                  }}
                  data-testid="join-link-share-btn"
                  className="shrink-0 px-4 py-2 rounded-full border border-white/15 hover:border-white/30 text-xs whitespace-nowrap"
                >
                  {linkCopied ? "¡Copiado!" : "Compartir link"}
                </button>
              </div>
            </div>

            <div className="glass rounded-3xl p-8 border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[var(--brand-cyan,#FF85AC)]/20 border border-[var(--brand-cyan,#FF85AC)] flex items-center justify-center">
                  <Check className="w-4 h-4 text-[var(--brand-cyan,#FF85AC)]" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--brand-cyan,#FF85AC)]">SOS Desbloqueado (para ti)</p>
                  <h2 className="font-display text-2xl">{group.name}</h2>
                </div>
              </div>
              <p className="text-neutral-400 mb-6">
                {(group.members?.length || 0) + 1} personas en el grupo · tu celular ya puede activar el SOS.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => navigate("/sos")}
                  data-testid="go-sos-btn"
                  className="bg-[#FF3B30] text-white px-7 py-3 rounded-full font-semibold hover:bg-[#FF5147] danger-glow"
                >
                  Ir al Botón SOS
                </button>
                <button
                  onClick={() => { localStorage.removeItem(STORAGE_GROUP); setGroup(null); setStep("create"); }}
                  className="px-7 py-3 rounded-full border border-white/15 hover:border-white/30 text-sm"
                >
                  Crear otro grupo
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </Layout>
  );
}
